import { useState, useEffect, useCallback, createContext, useContext } from 'react';

// Contexto para autenticação
const AuthContext = createContext();

// Hook personalizado para usar o contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
};

/**
 * Hook personalizado para gerenciar autenticação e sincronizar com preferências
 * Resolve o problema de carregamento das preferências após login
 */
const useAuthLogic = () => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Função para carregar dados do usuário
  const loadUser = useCallback(() => {
    try {
      const stored = localStorage.getItem("usuario");
      if (stored) {
        const userData = JSON.parse(stored);
        const userInfo = {
          id: userData.id || userData.st_id, // Fallback para compatibilidade
          nome_completo: userData.st_nome,
          email: userData.st_email,
          // Outros campos que possam existir
          ...userData
        };
        
        setUser(userInfo);
        setIsAuthenticated(true);
        return userInfo;
      } else {
        setUser(null);
        setIsAuthenticated(false);
        return null;
      }
    } catch (error) {
      console.error('❌ Erro ao carregar usuário:', error);
      setUser(null);
      setIsAuthenticated(false);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Função para fazer logout
  const logout = useCallback(() => {
    localStorage.clear();
    setUser(null);
    setIsAuthenticated(false);
    window.location.href = "/login";
  }, []);

  // Função para atualizar dados do usuário
  const updateUser = useCallback((newData) => {
    setUser(prev => ({ ...prev, ...newData }));
  }, []);

  // Carregar usuário ao inicializar
  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Listener para mudanças no localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "usuario") {
        loadUser();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Também escutar mudanças locais (para o mesmo tab)
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function(key, value) {
      originalSetItem.apply(this, arguments);
      if (key === "usuario") {
        setTimeout(() => loadUser(), 100); // Pequeno delay para garantir sincronização
      }
    };

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      localStorage.setItem = originalSetItem;
    };
  }, [loadUser]);

  return {
    user,
    isAuthenticated,
    isLoading,
    loadUser,
    logout,
    updateUser
  };
};

// Provider do contexto de autenticação
export const AuthProvider = ({ children }) => {
  const authLogic = useAuthLogic();
  
  return (
    <AuthContext.Provider value={authLogic}>
      {children}
    </AuthContext.Provider>
  );
};
